import {hideError} from "../actions/actions";
import axios, {type AxiosResponse} from "axios";
import {API_URL} from "../config";
import {
    GET_NOTIFICATION_ERROR,
} from "../types/types";
import {SET_USER_STATUS_ERROR, SET_USER_STATUS_SUCCESS} from "../types/typesUsersList";
import {showAccessDeniedNotifications} from "../actions/actionsNotifications";
import {
    GET_IVR_CONFIGURATION,
    GET_IVR_CONFIGURATIONS,
    GET_IVR_CONFIGURATION_PAGINATION,
    SHOW_IVR_CONFIGURATION_DETAIL_LOADING,
} from "../types/typesIVRConfigurationList";
import {
    setIVRConfigurationField,
    showIVRConfigurationLoader,
} from "../actions/actionsIVRConfiguration";
import {setUserError} from "../actions/actionsUsersList";
import {getQueueIVRType} from "./apiQueue";

export let ivrConfigurationsListDataSort: {
    pageNumber: number;
    pageSize: number;
    sort: { orderBy: string; isDesc: boolean };
    filter?: { key: string; value: string }[];
} = {
    pageNumber: 1,
    pageSize: 10,
    sort: {
        orderBy: "FlowId",
        isDesc: true,
    },
};

function parseIvrConfigurationsPaginationResponse(
    responseRoot: unknown,
    request: { pageNumber: number; pageSize: number }
): { items: unknown[]; hasNext: boolean; pageNumber: number } {
    const reqPage = Math.max(1, Number(request.pageNumber) || 1);
    const reqSize = Math.max(1, Number(request.pageSize) || 10);
    let items: unknown[] = [];
    let hasNext = false;
    let pageNumber = reqPage;

    if (responseRoot != null && typeof responseRoot === "object") {
        const root = responseRoot as Record<string, unknown>;
        const inner =
            root.data != null && typeof root.data === "object" && !Array.isArray(root.data)
                ? (root.data as Record<string, unknown>)
                : root.Data != null &&
                    typeof root.Data === "object" &&
                    !Array.isArray(root.Data)
                  ? (root.Data as Record<string, unknown>)
                  : null;

        const bag = inner ?? root;

        const rawItems = bag.items ?? bag.Items;
        if (Array.isArray(rawItems)) {
            items = rawItems;
        }

        const hn = bag.hasNext ?? bag.HasNext;
        if (typeof hn === "boolean") {
            hasNext = hn;
        } else if (hn === 1 || hn === "1" || hn === "true") {
            hasNext = true;
        }

        const pn = bag.pageNumber ?? bag.PageNumber;
        if (pn != null && pn !== "") {
            const n = Number(pn);
            if (Number.isFinite(n) && n >= 1) {
                pageNumber = Math.floor(n);
            }
        }

        const totalKnown = Number(
            bag.totalItems ??
                bag.TotalItems ??
                bag.totalCount ??
                bag.TotalCount ??
                bag.totalRecords ??
                bag.TotalRecords ??
                0
        );
        if (
            totalKnown > 0 &&
            !("hasNext" in bag) &&
            !("HasNext" in bag)
        ) {
            hasNext = pageNumber * reqSize < totalKnown;
        }
    }

    return {items, hasNext, pageNumber};
}

export function getIvrConfigurationsWithPagination(data: any, jwtToken: any) {
    return (dispatch: (arg0: any) => void) => {
        dispatch(showIVRConfigurationLoader(true));
        dispatch(hideError());
        const body: Record<string, unknown> = {
            pageNumber: data.pageNumber,
            pageSize: data.pageSize,
            sort: data.sort,
        };
        if (Array.isArray(data.filter) && data.filter.length > 0) {
            body.filter = data.filter;
        }
        axios
            .post(`${API_URL}IVRConfig/GetIvrConfigurationsWithPagination`, body, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${jwtToken}`,
                },
            })
            .then((response) => {
                ivrConfigurationsListDataSort = data;
                const {items, hasNext, pageNumber} =
                    parseIvrConfigurationsPaginationResponse(response.data, {
                        pageNumber: data.pageNumber,
                        pageSize: data.pageSize,
                    });
                dispatch({
                    type: GET_IVR_CONFIGURATIONS,
                    payload: items,
                });
                dispatch({
                    type: GET_IVR_CONFIGURATION_PAGINATION,
                    payload: {hasNext, pageNumber},
                });
            })
            .catch((e) => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    dispatch({type: GET_NOTIFICATION_ERROR, payload: true});
                    dispatch(setUserError(true));
                }
            })
            .finally(() => {
                dispatch(showIVRConfigurationLoader(false));
                dispatch(showAccessDeniedNotifications(false));
            });
    };
}

function ivrApiRequestFinally(dispatch: (action: unknown) => void) {
    dispatch(showAccessDeniedNotifications(false));
    dispatch({type: SHOW_IVR_CONFIGURATION_DETAIL_LOADING, payload: false});
    dispatch({type: SET_USER_STATUS_SUCCESS, payload: null});
    dispatch({type: SET_USER_STATUS_ERROR, payload: null});
    dispatch({type: GET_NOTIFICATION_ERROR, payload: false});
    return dispatch(setUserError(false));
}

export type IvrConfigCreateElementPayload = {
    flowId: number;
    elementId?: number;
    fromElementId?: number;
    name: string | null;
    elementType: string;
    audios?: { audioId: number; audioName: string }[] | null;
    elementData?: unknown;
    draftData?: string | null;
    minDigits?: number | null;
    maxDigits?: number | null;
    terminators?: unknown;
    regexp?: string | null;
    digitTimeOut?: number | null;
    timeOut?: number | null;
    silencePre?: number | null;
    silencePost?: number | null;
    schedules?: IvrConfigScheduleRowPayload[];
    updateSchedulers?: IvrConfigSchedulerUpdatePayload[];
};

export type IvrConfigScheduleRowPayload = {
    flowId: number;
    elementId: number;
    priority: number;
    daysMask: number;
    fromTime: string;
    toTime: string;
    activeToId: number;
};

export type IvrConfigSchedulePostPayload = IvrConfigScheduleRowPayload & {
    isActive?: boolean;
};

function ivrMutationResponseIsSuccess(
    raw: unknown,
    httpStatus?: number
): boolean {
    if (httpStatus === 204) return true;
    if (httpStatus === 200 && (raw === "" || raw == null)) return true;
    if (raw == null || typeof raw !== "object") return false;
    const data = raw as Record<string, unknown>;
    if (data.statusCode === 200) return true;
    if (data.isDeleted === true) return true;
    const inner = data.data;
    if (inner != null && typeof inner === "object") {
        const innerObj = inner as Record<string, unknown>;
        if (innerObj.statusCode === 200) return true;
        if (innerObj.isDeleted === true) return true;
    }
    return false;
}

export function ivrConfigMutationRequest(
    dispatch: (action: unknown) => void,
    request: Promise<AxiosResponse<unknown>>
): Promise<AxiosResponse<unknown> | false> {
    return request
        .then((response) => {
            if (ivrMutationResponseIsSuccess(response.data, response.status)) {
                return response;
            }
            const data = response.data as {message?: string};
            dispatch({
                type: SET_USER_STATUS_ERROR,
                payload: {message: data.message},
            });
            return false;
        })
        .catch((e: unknown): false => {
            const err = e as {response?: {status?: number}};
            if (err?.response?.status === 401) {
                dispatch(showAccessDeniedNotifications(true));
            } else {
                dispatch({type: GET_NOTIFICATION_ERROR, payload: true});
                dispatch(setUserError(true));
            }
            return false;
        })
        .finally(() => {
            ivrApiRequestFinally(dispatch);
        });
}

export function postIvrConfigSchedule(
    payload: IvrConfigSchedulePostPayload,
    jwtToken: string | undefined
) {
    return axios.post(`${API_URL}IVRConfig/schedule`, payload, {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
        },
    });
}

export type IvrConfigSchedulePutPayload = {
    id: number;
    daysMask?: number;
    fromTime?: string;
    toTime?: string;
    activeToId?: number;
    isActive?: boolean;
};

export type IvrConfigSchedulerUpdatePayload = {
    id: number;
    daysMask: number;
    fromTime: string;
    toTime: string;
    isActive: boolean;
};

export function putIvrConfigSchedule(
    payload: IvrConfigSchedulePutPayload,
    jwtToken: string | undefined
) {
    return axios.put(`${API_URL}IVRConfig/schedule`, payload, {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
        },
    });
}

export function deleteIvrConfigSchedule(
    scheduleId: number,
    jwtToken: string | undefined
) {
    return axios.delete(`${API_URL}IVRConfig/schedule/${scheduleId}`, {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
        },
    });
}

export function postIvrConfigElement(
    payload: IvrConfigCreateElementPayload,
    jwtToken: string | undefined
) {
    return axios.post(`${API_URL}IVRConfig/element`, payload, {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
        },
    });
}

export function putIvrConfigElement(
    payload: IvrConfigCreateElementPayload,
    jwtToken: string | undefined
) {
    return axios.put(`${API_URL}IVRConfig/element`, payload, {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
        },
    });
}

export function deleteIvrConfigElement(
    flowId: number,
    elementId: number,
    jwtToken: string | undefined
) {
    return axios.delete(
        `${API_URL}IVRConfig/element/${flowId}/${elementId}`,
        {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${jwtToken}`,
            },
        }
    );
}

export function deleteIvrConfigAudio(
    audioId: number,
    flowId: number,
    elementId: number,
    jwtToken: string | undefined
) {
    return (dispatch: (arg0: any) => void): Promise<boolean> => {
        dispatch(hideError());
        return axios
            .delete(
                `${API_URL}IVRConfig/DeleteIVRAudio/${audioId}/${flowId}/${elementId}`,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${jwtToken}`,
                    },
                }
            )
            .then((response) => {
                if (response.data.statusCode === 200) {
                    return true;
                } else {
                    dispatch({type: SET_USER_STATUS_ERROR, payload: {message: response.data.message}});
                    return false;
                }
            })
            .catch((e): boolean => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    dispatch({type: GET_NOTIFICATION_ERROR, payload: true});
                    dispatch(setUserError(true));
                }
                return false;
            })
            .finally(() => {
                ivrApiRequestFinally(dispatch);
            });
    };
}

export type IvrTempAudioResult = {
    tempUrl: string;
    fileName: string;
};

export async function getIvrTempAudio(
    audioId: number,
    jwtToken: any
): Promise<IvrTempAudioResult | null> {
    const response = await axios.get(`${API_URL}IVRConfig/GetTempIVRAudio`, {
        params: { audioId },
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
        },
    });
    const d = response.data?.data;
    if (d?.tempUrl) return { tempUrl: d.tempUrl, fileName: d.fileName ?? "" };
    return null;
}


export type IvrConfigCreateRoutePayload = {
    flowId: number;
    fromElementId: number;
    routeType: string;
    routeValue: string;
    toElementId: number;
    priority: number;
    isActive: boolean;
    tries?: number | null;
};

export function postIvrConfigRoute(
    payload: IvrConfigCreateRoutePayload,
    jwtToken: string | undefined
) {
    return axios.post(`${API_URL}IVRConfig/route`, payload, {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
        },
    });
}

export function deleteIvrConfigRoute(
    routeId: number,
    jwtToken: string | undefined
) {
    return axios.delete(`${API_URL}IVRConfig/route/${routeId}`, {
        headers: {
            Authorization: `Bearer ${jwtToken}`,
        },
    });
}

export type IvrConfigPutConfigurationPayload = {
    flowId: number | string;
    entryElementId: number;
    name: string | null;
    description: string | null;
    branchId: number | null;
};

export function postIvrConfigConfiguration(
    payload: IvrConfigPutConfigurationPayload,
    jwtToken: string | undefined
) {
    return axios.post(`${API_URL}IVRConfig/configuration`, payload, {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
        },
    });
}

export function postCreateTrunk(
    payload: Record<string, unknown>,
    jwtToken: string | undefined
) {
    return axios.post(`${API_URL}Trunks/CreateTrunk`, payload, {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
        },
    });
}

export function putIvrConfigConfiguration(
    payload: IvrConfigPutConfigurationPayload,
    jwtToken: string | undefined
) {
    return axios.put(`${API_URL}IVRConfig/configuration`, payload, {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
        },
    });
}

function extractFlowIdFromIvrConfigurationMutationResponse(data: unknown): number | null {
    if (data == null) return null;
    if (typeof data === "number" && Number.isFinite(data)) return data;
    if (typeof data !== "object" || Array.isArray(data)) return null;
    const o = data as Record<string, unknown>;
    const conf = o.configuration as Record<string, unknown> | undefined;
    const candidates = [o.flowId, o.FlowId, o.id, conf?.flowId, conf?.FlowId];
    for (const c of candidates) {
        if (typeof c === "number" && Number.isFinite(c)) return c;
        if (typeof c === "string" && c !== "") {
            const n = Number(c);
            if (Number.isFinite(n)) return n;
        }
    }
    return null;
}

function buildIvrConfigConfigurationPayloadFromDialogState(
    data: Record<string, unknown>,
    mode: "create" | "update"
): IvrConfigPutConfigurationPayload {
    const conf = (data.configuration ?? data) as Record<string, unknown>;
    if (mode === "create") {
        return {
            flowId: "",
            entryElementId: 0,
            name: (conf.name as string | null) ?? null,
            description: (conf.description as string | null) ?? null,
            branchId: conf.branchId != null ? Number(conf.branchId) : null,
        };
    }
    return {
        flowId: conf.flowId as number | string,
        entryElementId: Number(conf.entryElementId ?? 0),
        name: (conf.name as string | null) ?? null,
        description: (conf.description as string | null) ?? null,
        branchId: conf.branchId != null ? Number(conf.branchId) : null,
    };
}

export function getIvrConfigValidateConfiguration(
    flowId: number | string,
    jwtToken: string | undefined
) {
    return axios.get(`${API_URL}IVRConfig/validateConfiguration/${flowId}`, {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
        },
    });
}

function normalizeGetIvrConfigurationByIdPayload(
    raw: unknown
): Record<string, unknown> | null {
    if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
        return null;
    }
    const o = raw as Record<string, unknown>;
    const hasNestedConfiguration =
        o.configuration != null &&
        typeof o.configuration === "object" &&
        !Array.isArray(o.configuration);
    const configuration = hasNestedConfiguration
        ? (o.configuration as Record<string, unknown>)
        : o;
    return {
        configuration,
        elements: Array.isArray(o.elements) ? o.elements : [],
        routes: Array.isArray(o.routes) ? o.routes : [],
        schedules: Array.isArray(o.schedules) ? o.schedules : [],
    };
}

export function validateIVRConfiguration(
    id: number | string,
    jwtToken: string | undefined
) {
    return (dispatch: (arg0: any) => void) => {
        dispatch(hideError());
        return getIvrConfigValidateConfiguration(id, jwtToken)
            .then((response) => {
                const root = response.data as Record<string, unknown> | undefined;
                const inner =
                    root != null &&
                    typeof root.data === "object" &&
                    root.data !== null &&
                    "isValid" in (root.data as object)
                        ? (root.data as Record<string, unknown>)
                        : null;
                const validation = inner ?? (root != null && "isValid" in root ? root : null);
                const isValid = validation?.isValid === true;
                const rootMessage =
                    typeof root?.message === "string" ? root.message : undefined;
                const joinDetail = (arr: unknown): string | undefined => {
                    if (!Array.isArray(arr) || arr.length === 0) {
                        return undefined;
                    }
                    const parts = arr
                        .map((item) =>
                            typeof item === "string"
                                ? item
                                : item != null && typeof item === "object" && "message" in item
                                  ? String((item as { message: unknown }).message)
                                  : String(item)
                        )
                        .filter(Boolean);
                    return parts.length ? parts.join("; ") : undefined;
                };

                if (isValid) {
                    dispatch({
                        type: SET_USER_STATUS_SUCCESS,
                        payload: {
                            message:
                                rootMessage ??
                                (typeof validation?.message === "string"
                                    ? validation.message
                                    : undefined) ??
                                "Схема готова до використання.",
                        },
                    });
                } else {
                    const detail =
                        joinDetail(validation?.errors) ??
                        joinDetail(validation?.issues) ??
                        joinDetail(validation?.warnings);
                    dispatch({
                        type: SET_USER_STATUS_ERROR,
                        payload: {
                            message:
                                rootMessage ??
                                detail ??
                                "Помилка перевірки схеми.",
                        },
                    });
                }
            })
            .catch((e) => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    dispatch({
                        type: SET_USER_STATUS_ERROR,
                        payload: {
                            message:
                                e?.response?.data?.message ??
                                "Помилка перевірки схеми.",
                        },
                    });
                    dispatch({type: GET_NOTIFICATION_ERROR, payload: true});
                    dispatch(setUserError(true));
                }
            })
            .finally(() => {
                dispatch(showAccessDeniedNotifications(false));
            });
    };
}

export function getIVRConfigurationById(id: any, jwtToken: any) {
    return (dispatch: (arg0: any) => void) => {
        dispatch({type: SHOW_IVR_CONFIGURATION_DETAIL_LOADING, payload: true});
        dispatch(hideError());
        const flowId = encodeURIComponent(String(id));
        axios
            .get(`${API_URL}IVRConfig/GetIvrConfigurationById?flowId=${flowId}`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${jwtToken}`,
                },
            })
            .then((response) => {
                if (response.data.statusCode === 200) {
                    const normalized = normalizeGetIvrConfigurationByIdPayload(
                        response.data.data
                    );
                    if (
                        normalized != null &&
                        normalized.configuration != null &&
                        typeof normalized.configuration === "object"
                    ) {
                        dispatch({
                            type: GET_IVR_CONFIGURATION,
                            payload: {
                                ...normalized,
                                _schemeReloadToken: Date.now(),
                            },
                        });
                    } else {
                        dispatch({
                            type: SET_USER_STATUS_ERROR,
                            payload: {message: response.data.message},
                        });
                    }
                } else {
                    dispatch({
                        type: SET_USER_STATUS_ERROR,
                        payload: {message: response.data.message},
                    });
                }
            })
            .catch((e) => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    dispatch({type: GET_NOTIFICATION_ERROR, payload: true});
                    return dispatch(setUserError(true));
                }
            })
            .finally(() => {
                ivrApiRequestFinally(dispatch);
            });
    };
}

/** Повна конфігурація з елементами / маршрутами / розкладами — лише для редактора схеми. */
export function getIVRConfigurationFullById(id: any, jwtToken: any) {
    return (dispatch: (arg0: any) => void) => {
        dispatch({type: SHOW_IVR_CONFIGURATION_DETAIL_LOADING, payload: true});
        dispatch(hideError());
        axios
            .get(`${API_URL}IVRConfig/${id}/full`, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${jwtToken}`,
                },
            })
            .then((response) => {
                if (response.data.statusCode === 200) {
                    const d = response.data.data;
                    dispatch({
                        type: GET_IVR_CONFIGURATION,
                        payload: {
                            ...d,
                            _schemeReloadToken: Date.now(),
                        },
                    });
                } else {
                    dispatch({
                        type: SET_USER_STATUS_ERROR,
                        payload: {message: response.data.message},
                    });
                }
            })
            .catch((e) => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    dispatch({type: GET_NOTIFICATION_ERROR, payload: true});
                    return dispatch(setUserError(true));
                }
            })
            .finally(() => {
                ivrApiRequestFinally(dispatch);
            });
    };
}

export function createIvrConfigElement(
    payload: any,
    jwtToken: any
) {
    return (dispatch: (arg0: any) => void) => {
        dispatch(showIVRConfigurationLoader(true));
        dispatch(hideError());
        ivrConfigMutationRequest(
            dispatch,
            postIvrConfigElement(payload, jwtToken) as Promise<AxiosResponse<unknown>>
        )
            .then((result) => {
                if (result !== false) {
                    dispatch(getIVRConfigurationFullById(payload.flowId, jwtToken));
                }
            })
            .finally(() => {
                dispatch(showIVRConfigurationLoader(false));
            });
    };
}

export function createIVRConfiguration(data: any, jwtToken: any) {
    return (dispatch: (arg0: any) => void) => {
        dispatch(showIVRConfigurationLoader(true))
        dispatch(hideError())
        const payload = buildIvrConfigConfigurationPayloadFromDialogState(
            data,
            "create"
        );
        postIvrConfigConfiguration(payload, jwtToken)
            .then((response) => {
                if (response.data.statusCode === 200) {
                    dispatch(getQueueIVRType(jwtToken));
                    dispatch(getIvrConfigurationsWithPagination(ivrConfigurationsListDataSort, jwtToken));
                    const newId = extractFlowIdFromIvrConfigurationMutationResponse(
                        response?.data?.data
                    );
                    if (newId != null) {
                        dispatch(getIVRConfigurationById(newId, jwtToken));
                    }
                } else {
                    dispatch({type: SET_USER_STATUS_ERROR, payload: {message: response.data.message}});
                }
            })
            .catch((e) => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    dispatch({type: GET_NOTIFICATION_ERROR, payload: true});
                    return dispatch(setUserError(true));
                }
            })
            .finally(() => {
                dispatch(showIVRConfigurationLoader(false));
                ivrApiRequestFinally(dispatch);
            })
    }
}

export function updateIVRConfiguration(data: any, jwtToken: any) {
    return (dispatch: (arg0: any) => void) => {
        dispatch(showIVRConfigurationLoader(true))
        dispatch(hideError())
        const payload = buildIvrConfigConfigurationPayloadFromDialogState(
            data,
            "update"
        );
        putIvrConfigConfiguration(payload, jwtToken)
            .then((response) => {
                if (response.data.statusCode === 200) {
                    dispatch(getQueueIVRType(jwtToken));
                    dispatch(getIvrConfigurationsWithPagination(ivrConfigurationsListDataSort, jwtToken));
                    const fromBody = extractFlowIdFromIvrConfigurationMutationResponse(
                        response?.data?.data
                    );
                    const flowIdNum = Number(payload.flowId);
                    const id =
                        fromBody ??
                        (Number.isFinite(flowIdNum) ? flowIdNum : null);
                    if (id != null) {
                        dispatch(getIVRConfigurationById(id, jwtToken));
                    }
                } else {
                    dispatch({type: SET_USER_STATUS_ERROR, payload: {message: response.data.message}});
                }
            })
            .catch((e) => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    dispatch({type: GET_NOTIFICATION_ERROR, payload: true});
                    return dispatch(setUserError(true));
                }
            })
            .finally(() => {
                dispatch(showIVRConfigurationLoader(false));
                ivrApiRequestFinally(dispatch);
            })
    }
}

export function putIvrConfigActivateDeactivate(
    flowId: number,
    isActive: boolean,
    jwtToken: string | undefined
) {
    return axios.put(
        `${API_URL}IVRConfig/activateDeactivateConfiguration/${flowId}`,
        {},
        {
            params: {isActive},
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${jwtToken}`,
            },
        }
    );
}

export function activateDeactivateIVRConfiguration(
    flowId: number,
    isActive: boolean,
    jwtToken: any
) {
    return (dispatch: (arg0: any) => void) => {
        dispatch(hideError());
        return putIvrConfigActivateDeactivate(flowId, isActive, jwtToken)
            .then((response) => {
                if (response.data.statusCode === 200) {
                    dispatch(
                        setIVRConfigurationField({
                            fieldName: "isActive",
                            value: isActive,
                        })
                    );
                    dispatch(getQueueIVRType(jwtToken));
                    dispatch(getIvrConfigurationsWithPagination(ivrConfigurationsListDataSort, jwtToken));
                    return true;
                }
                dispatch({
                    type: SET_USER_STATUS_ERROR,
                    payload: {message: response.data.message},
                });
                return false;
            })
            .catch((e) => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    dispatch({type: GET_NOTIFICATION_ERROR, payload: true});
                    dispatch(setUserError(true));
                }
                return false;
            })
            .finally(() => {
                ivrApiRequestFinally(dispatch);
            });
    };
}

export function copyIVRConfiguration(flowId: number, jwtToken: any) {
    return (dispatch: (arg0: any) => void) => {
        dispatch(showIVRConfigurationLoader(true));
        dispatch(hideError());
        axios
            .put(`${API_URL}IVRConfig/CopyIVRConfiguration`, {}, {
                params: {flowId},
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${jwtToken}`,
                },
            })
            .then((response) => {
                if (response.data.statusCode === 200) {
                    dispatch({
                        type: SET_USER_STATUS_SUCCESS,
                        payload: {message: response.data.message},
                    });
                    dispatch(getQueueIVRType(jwtToken));
                    dispatch(getIvrConfigurationsWithPagination(ivrConfigurationsListDataSort, jwtToken));
                } else {
                    dispatch({type: SET_USER_STATUS_ERROR, payload: {message: response.data.message}});
                }
            })
            .catch((e) => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    dispatch({type: GET_NOTIFICATION_ERROR, payload: true});
                    return dispatch(setUserError(true));
                }
            })
            .finally(() => {
                dispatch(showIVRConfigurationLoader(false));
                ivrApiRequestFinally(dispatch);
            });
    };
}

export function deleteIvrConfigConfiguration(
    flowId: number,
    jwtToken: string | undefined
) {
    return axios.delete(`${API_URL}IVRConfig/configuration/${flowId}`, {
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwtToken}`,
        },
    });
}

export function deleteIVRConfiguration(id: number, jwtToken: any) {
    return (dispatch: (arg0: any) => void) => {
        dispatch(showIVRConfigurationLoader(true));
        dispatch(hideError());
        deleteIvrConfigConfiguration(id, jwtToken)
            .then((response) => {
                if (response.data.statusCode === 200) {
                    dispatch({
                        type: SET_USER_STATUS_SUCCESS,
                        payload: {message: response.data.message},
                    });
                    dispatch(getQueueIVRType(jwtToken));
                    dispatch(getIvrConfigurationsWithPagination(ivrConfigurationsListDataSort, jwtToken));
                } else {
                    dispatch({type: SET_USER_STATUS_ERROR, payload: {message: response.data.message}});
                }
            })
            .catch((e) => {
                if (e?.response?.status === 401) {
                    dispatch(showAccessDeniedNotifications(true));
                } else {
                    dispatch({type: GET_NOTIFICATION_ERROR, payload: true});
                    return dispatch(setUserError(true));
                }
            })
            .finally(() => {
                dispatch(showIVRConfigurationLoader(false));
                ivrApiRequestFinally(dispatch);
            });
    };
}

